import {
  ChangeEvent,
  FC,
  InputEvent,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { AlgorithmSourceFormDataType } from '../sources/schema/types';
import type { AlgorithmMappingQueryDtoType } from '../mappings/schema/types';
import { FormField, useGetIdFromName } from '../../../common/form';
import {
  decodeRuleSourceItemValue,
  encodeRuleAlgorithmMappingValue,
  encodeRuleAlgorithmSourceValue,
} from './utils';
import { getAlgorithmSourceShortLabel } from '../utils';
import { Select } from '../../../common/components';
import { Input, Space } from 'antd';
import { useFormContext, useWatch } from 'react-hook-form';

interface AlgorithmTargetItemDbSourceMappingSelectProps {
  index: number;
  label: string;
  sources: AlgorithmSourceFormDataType[];
  mappings: AlgorithmMappingQueryDtoType[];
}

export const AlgorithmTargetItemDbSourceMappingSelect: FC<
  AlgorithmTargetItemDbSourceMappingSelectProps
> = (props) => {
  const { index, label, sources, mappings } = props;
  const { setValue } = useFormContext();
  const sourceItemName = `rules.${index}.sourceItem` as const;
  const sourceItemCodeName = `rules.${index}.sourceItemCode` as const;
  const sourceItem = useWatch({ name: sourceItemName });
  const sourceItemCodeFieldValue = useWatch({ name: sourceItemCodeName });
  const getIdFromName = useGetIdFromName();

  const decodedSourceItem = useMemo(
    () => decodeRuleSourceItemValue(sourceItem),
    [sourceItem],
  );

  const sourceReferenceValue = sourceItem || undefined;
  const isSourceSelected = 'algorithmSourceId' in decodedSourceItem;
  const sourceItemCode =
    sourceItemCodeFieldValue ??
    (isSourceSelected ? decodedSourceItem.sourceItemCode : '');

  const sourceReferenceOptions = useMemo(
    () => [
      ...sources.map((source) => ({
        value: encodeRuleAlgorithmSourceValue(source.id, sourceItemCode),
        label: getAlgorithmSourceShortLabel(source),
      })),
      ...mappings.map((mapping) => ({
        value: encodeRuleAlgorithmMappingValue(mapping.id),
        label: mapping.alias,
      })),
    ],
    [sources, mappings, sourceItemCode],
  );

  // Set default selection
  useEffect(() => {
    if (sourceReferenceValue || sourceReferenceOptions.length === 0) {
      return;
    }

    const initialValue = String(sourceReferenceOptions[0].value);
    setValue(sourceItemName, initialValue, { shouldDirty: false });

    if (initialValue.startsWith('m-')) {
      setValue(sourceItemCodeName, '', { shouldDirty: false });
    } else {
      const decodedInitialValue = decodeRuleSourceItemValue(initialValue);
      const initialSourceItemCode =
        'algorithmSourceId' in decodedInitialValue
          ? decodedInitialValue.sourceItemCode
          : '';
      setValue(sourceItemCodeName, initialSourceItemCode, {
        shouldDirty: false,
      });
    }
  }, [
    sourceReferenceOptions,
    sourceReferenceValue,
    setValue,
    sourceItemCodeName,
    sourceItemName,
  ]);

  const handleSourceReferenceChange = useCallback(
    (value: string | number | null) => {
      if (!value) {
        setValue(sourceItemName, '');
        setValue(sourceItemCodeName, '');
        setValue(`rules.${index}.subjectFieldCodeId`, null);
        return;
      }

      const selected = String(value);

      setValue(sourceItemName, selected);

      if (selected.startsWith('m-')) {
        setValue(sourceItemCodeName, '');
        setValue(`rules.${index}.subjectFieldCodeId`, null);
        return;
      }

      if (selected.startsWith('s-')) {
        const decodedSelected = decodeRuleSourceItemValue(selected);
        const nextSourceItemCode =
          'algorithmSourceId' in decodedSelected
            ? decodedSelected.sourceItemCode
            : '';
        setValue(sourceItemCodeName, nextSourceItemCode);
        setValue(`rules.${index}.subjectFieldCodeId`, null);
      }
    },
    [setValue, sourceItemName, sourceItemCodeName, index],
  );

  const handleSourceItemCodeChange = useCallback(
    (value: string) => {
      if (!isSourceSelected) return;

      setValue(
        sourceItemName,
        encodeRuleAlgorithmSourceValue(
          decodedSourceItem.algorithmSourceId,
          value,
        ),
      );
    },
    [decodedSourceItem, isSourceSelected, setValue, sourceItemName],
  );

  return (
    <FormField
      required
      name={sourceItemName}
      label={label}
      className="nodrag nopan"
      style={{ minWidth: 0 }}
    >
      <Space.Compact style={{ width: '100%' }} className="nodrag nopan">
        <Select
          id={getIdFromName(sourceItemName).replace(
            'sourceItem',
            'sourceReference',
          )}
          options={sourceReferenceOptions}
          value={sourceReferenceValue}
          onChange={handleSourceReferenceChange}
          style={{ width: 'max-content' }}
        />
        <Input
          id={getIdFromName(sourceItemCodeName)}
          value={sourceItemCode}
          disabled={!isSourceSelected}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value.replace(/\./g, '');
            setValue(sourceItemCodeName, value, { shouldDirty: true });
            handleSourceItemCodeChange(value);
          }}
          onBeforeInput={(event: InputEvent<HTMLInputElement>) => {
            if (event.data === '.') {
              event.preventDefault();
            }
          }}
        />
      </Space.Compact>
    </FormField>
  );
};
